import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const transactionsApi = createApi({
  reducerPath: "transactionsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Transactions"],
  endpoints: (build) => ({
    // Get all transactions for the user
    getTransactions: build.query({
      query: () => ({
        url: "/transactions",
        method: "GET",
      }),
      providesTags: ["Transactions"],
      transformResponse: (response) => response.data || response,
    }),

    // Get transaction by ID
    getTransactionById: build.query({
      query: (id) => ({
        url: `/transactions/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Transactions", id }],
      transformResponse: (response) => response.data || response,
    }),

    // Get transactions by status
    getTransactionsByStatus: build.query({
      query: (status) => ({
        url: `/transactions/status/${status}`,
        method: "GET",
      }),
      providesTags: ["Transactions"],
      transformResponse: (response) => response.data || response,
    }),

    // Get transaction stats
    getTransactionStats: build.query({
      query: () => ({
        url: "/transactions/stats",
        method: "GET",
      }),
      providesTags: ["Transactions"],
      transformResponse: (response) => response.data || response,
    }),

    // Get upcoming closings
    getUpcomingClosings: build.query({
      query: ({ days = 30 } = {}) => ({
        url: "/transactions/upcoming-closings",
        method: "GET",
        params: { days },
      }),
      providesTags: ["Transactions"],
      transformResponse: (response) => response.data || response,
    }),

    // Create transaction
    createTransaction: build.mutation({
      query: (body) => ({
        url: "/transactions",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Transactions"],
    }),

    // Update transaction
    updateTransaction: build.mutation({
      query: ({ id, ...body }) => ({
        url: `/transactions/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Transactions", id },
        "Transactions",
      ],
    }),

    // Delete transaction
    deleteTransaction: build.mutation({
      query: (id) => ({
        url: `/transactions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Transactions"],
    }),

    // Add document to transaction
    addDocument: build.mutation({
      query: ({ transactionId, ...document }) => ({
        url: `/transactions/${transactionId}/documents`,
        method: "POST",
        body: document,
      }),
      invalidatesTags: (result, error, { transactionId }) => [
        { type: "Transactions", id: transactionId },
      ],
    }),

    // Update document status
    updateDocumentStatus: build.mutation({
      query: ({ transactionId, documentId, ...body }) => ({
        url: `/transactions/${transactionId}/documents/${documentId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { transactionId }) => [
        { type: "Transactions", id: transactionId },
      ],
    }),

    // Add checklist item
    addChecklistItem: build.mutation({
      query: ({ transactionId, ...item }) => ({
        url: `/transactions/${transactionId}/checklist`,
        method: "POST",
        body: item,
      }),
      invalidatesTags: (result, error, { transactionId }) => [
        { type: "Transactions", id: transactionId },
      ],
    }),

    // Update checklist item
    updateChecklistItem: build.mutation({
      query: ({ transactionId, itemId, ...body }) => ({
        url: `/transactions/${transactionId}/checklist/${itemId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { transactionId }) => [
        { type: "Transactions", id: transactionId },
      ],
    }),

    // Add timeline event
    addTimelineEvent: build.mutation({
      query: ({ transactionId, ...event }) => ({
        url: `/transactions/${transactionId}/timeline`,
        method: "POST",
        body: event,
      }),
      invalidatesTags: (result, error, { transactionId }) => [
        { type: "Transactions", id: transactionId },
      ],
    }),
  }),
});

export const {
  useGetTransactionsQuery,
  useGetTransactionByIdQuery,
  useGetTransactionsByStatusQuery,
  useGetTransactionStatsQuery,
  useGetUpcomingClosingsQuery,
  useCreateTransactionMutation,
  useUpdateTransactionMutation,
  useDeleteTransactionMutation,
  useAddDocumentMutation,
  useUpdateDocumentStatusMutation,
  useAddChecklistItemMutation,
  useUpdateChecklistItemMutation,
  useAddTimelineEventMutation,
} = transactionsApi;
